import * as d3 from 'd3';

/**
 * D3 Tree Layout
 *
 * Resources
 *
 * https://observablehq.com/@d3/collapsible-tree
 * https://bl.ocks.org/d3noob/43a860bc0024792f8803bba8ca0d5ecd
 * https://codepen.io/EleftheriaBatsou/pen/LmddvN
 */

class TreeNav {
  constructor() {
    this.options = {
      radius: 3,
      depth: 350,
      duration: 500,
      initialSpacing: 65,
      lineWrap: 280,
      nodeSpacing: 50,
    };

    this.colors = {
      black: '#000000',
      gray: '#f3f2f1',
      red: '#ff4637',
      white: '#ffffff',
    };

    this.REST_URL = '/wp-json/wp/v2/tree-nav';

    this.dragListener = '';
    this.coords = 0;
    this.direction = '';
    this.dragged = 0;
    this.isDragging = false;
    this.isDraggable = false;
    this.scrollEnd = 0;

    this.startX = 0;
    this.currentDrag = 70;
    this.dragDistance = 0;

    this.container = document.querySelector('.tree-nav__nav');
    this.margin = { top: 20, right: 90, bottom: 30, left: 90 };
    this.width = 0;
    this.height = 0;
    this.newHeight = 0;
    this.bounds = 0;
    this.root = '';
    this.svg = '';
    this.i = 0;
    this.treemap = '';
    this.treeData = '';
    this.nodes = '';
    this.node = '';
    this.links = '';
    this.data = null;

    this.isTreeMap = false;

    this.promiseLink = '';

    this.bindEvents();
    this.initialize();
  }

  initialize() {
    if (!this.data && window.innerWidth > 600 && !this.isTreeMap) {
      this.isTreeMap = true;
      this.getData();
    }
  }

  /**
   * Get data from WP REST API
   */
  getData() {
    d3.json(this.REST_URL).then(data => {
      this.data = data;
      this.initialHeight = this.data.children.length * this.options.initialSpacing;

      if (this.initialHeight) {
        this.setup();
      }
    });
  }

  bindEvents() {
    window.addEventListener('resize', this.windowResize.bind(this));
  }

  dragStart() {
    this.startX = d3.event.x;

    if (this.isDraggable) {
      this.container.classList.add('tree-nav--is-dragging');
    }
  }

  drag() {
    const distance = d3.event.x - this.startX;

    if (d3.event.x > this.startX) {
      this.direction = 'BACKWARD';
    } else if (d3.event.x < this.startX) {
      this.direction = 'FORWARD';
    }

    if (this.direction === 'BACKWARD') {
      if (!this.isDragging) {
        if (this.currentDrag <= 70) {
          this.isDragging = false;
          this.dragged = this.currentDrag + distance <= 70 ? this.currentDrag + distance : 70;
          d3.select('.tree-nav__group').attr('transform', `translate(${this.dragged}, ${0})`);
        } else {
          this.isDragging = true;
        }
      }
    }

    if (this.direction === 'FORWARD') {
      if (!this.isDragging) {
        if (this.currentDrag >= this.scrollEnd) {
          this.isDragging = false;
          this.dragged =
            this.currentDrag + distance >= this.scrollEnd
              ? this.currentDrag + distance
              : this.scrollEnd;
          d3.select('.tree-nav__group').attr('transform', `translate(${this.dragged}, ${0})`);
        }
      } else {
        this.isDragging = true;
      }
    }
  }

  dragEnd() {
    if (this.isDraggable) {
      this.container.classList.remove('tree-nav--is-dragging');
    }

    if (!this.isDragging) {
      this.currentDrag = this.dragged;
    }
  }

  windowResize() {
    if (this.data) {
      this.update(this.root);
      this.centerNode(this.root);
    }
    this.initialize();
  }

  /**
   * Sets up the D3 SVG
   */
  setup() {
    const dragListener = d3
      .drag()
      .on('start', this.dragStart.bind(this))
      .on('drag', this.drag.bind(this))
      .on('end', this.dragEnd.bind(this));

    // Set the dimensions and margins of the diagram
    this.width = 960 - this.margin.left - this.margin.right;
    this.height = this.initialHeight + this.margin.top + this.margin.bottom;

    this.svg = d3
      .select('.js--tree-nav-svg')
      .append('svg')
      .attr('width', this.width + this.margin.right + this.margin.left)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .call(dragListener)
      .append('g')
      .attr('class', 'tree-nav__group')
      .attr('role', 'navigation')
      .attr('transform', `translate(${70},${0})`);

    // declares a tree layout and assigns the size
    this.treemap = d3.tree().size([this.height, this.width]);

    // Assigns parent, children, height, depth
    this.root = d3.hierarchy(this.data, d => d.children);
    this.root.x0 = this.height / 2;
    this.root.y0 = 0;

    // Collapse after the second level
    this.root.children.forEach(this.collapse.bind(this));
    this.update(this.root);

    if (this.promiseLink) {
      this.promiseLink().then(() => this.transitionEnd(this.root));
    }
  }

  /**
   *
   * @param {Node} node containing D3 parent coordindates
   */
  update(source) {
    // Assigns the x and y position for the nodes
    this.treeData = this.treemap(this.root);

    // Compute the new tree layout.
    this.nodes = this.treeData.descendants();
    this.links = this.treeData.descendants().slice(1);

    // Normalize for fixed-depth.
    this.nodes.forEach(d => {
      d.y = d.depth * this.options.depth;
    });

    if (this.bounds.width > this.container.offsetWidth) {
      this.isDraggable = true;
      this.container.classList.add('tree-nav--is-draggable');
    } else {
      this.isDraggable = false;
      this.container.classList.remove('tree-nav--is-draggable');
    }

    // Update the nodes...
    this.node = this.svg
      .selectAll('g.tree-nav__node')
      .data(this.nodes, d => d.id || (d.id = ++this.i));

    // Enter any new nodes at the parent's previous position.
    const nodeEnter = this.node
      .enter()
      .append('g')
      .attr('class', 'tree-nav__node')
      .attr('transform', () => `translate(${source.y0}, ${source.x0})`);

    // Add Circle for the nodes
    nodeEnter
      .append('circle')
      .attr('class', d =>
        d._children || d.children ? 'tree-nav__node tree-nav__circle-has-hover' : 'tree-nav__node',
      )
      .attr('r', 1e-6)
      .style('fill', d => (d._children ? this.colors.black : this.colors.white));

    const _this = this;

    // Add labels for the nodes
    nodeEnter
      .append('text')
      .attr('id', d => {
        let name = d.data.name;
        name = name.replace(' ', '-');
        name = name.toLowerCase();
        return name;
      })
      .attr('class', d => (d.children || d._children ? 'tree-nav__has-hover' : ''))
      .attr('role', 'button')
      .attr('tabindex', 0)
      .attr('dy', '.35em')
      .attr('x', d => (d.children ? -13 : 13))
      .attr('text-length', 13)
      .attr('text-anchor', d => (d.children ? 'end' : 'start'))
      .each(function(d) {
        if (d.data.url !== '#' && d.data.url !== undefined) {
          d3.select(this)
            .text(d => d.data.name)
            .call(_this.wrap.bind(_this), _this.options.lineWrap, true, d => d.data.url)
            .on('click', _this.click.bind(_this))
            .on('keydown', d => {
              const { keyCode } = d3.event;
              // Listen for enter or space
              if (keyCode === 32 || keyCode === 13) {
                _this.click(d);
              }
            });
        } else {
          d3.select(this)
            .text(d => d.data.name)
            .call(_this.wrap.bind(_this), _this.options.lineWrap)
            .on('click', _this.click.bind(_this))
            .on('keydown', d => {
              const { keyCode } = d3.event;
              // Listen for enter or space
              if (keyCode === 32 || keyCode === 13) {
                _this.click(d);
              }
            });
        }
      });

    // UPDATE
    const nodeUpdate = nodeEnter.merge(this.node);

    // Transition to the proper position for the node
    nodeUpdate
      .transition()
      .duration(this.options.duration)
      .attr('transform', d => `translate(${d.y},${d.x})`);

    // Update the node attributes and style
    nodeUpdate
      .select('circle.tree-nav__node')
      .attr('r', this.options.radius)
      .style('fill', d => (d._children ? this.colors.black : this.colors.white));

    // Remove any exiting nodes
    const nodeExit = this.node
      .exit()
      .transition()
      .duration(this.options.duration)
      .attr('transform', () => `translate(${source.y},${source.x})`)
      .remove();

    // On exit reduce the node circles size to 0
    nodeExit.select('circle').attr('r', 1e-6);

    // On exit reduce the opacity of text labels
    nodeExit.select('text').style('fill-opacity', 1e-6);

    // Update the links...
    const link = this.svg.selectAll('path.tree-nav__link').data(this.links, d => d.id);

    // Enter any new links at the parent's previous position.
    const linkEnter = link
      .enter()
      .insert('path', 'g')
      .attr('class', 'tree-nav__link')
      .attr('d', () => {
        const o = { x: source.x0, y: source.y0 };
        return this.diagonal(o, o);
      });

    // UPDATE
    const linkUpdate = linkEnter.merge(link);

    // Transition back to the parent element position
    this.promiseLink = () =>
      new Promise(resolve => {
        linkUpdate
          .transition()
          .duration(this.options.duration)
          .attr('d', d => this.diagonal(d, d.parent))
          .on('end', resolve);
      });

    this.promiseLink();

    // Remove any exiting links
    link
      .exit()
      .transition()
      .duration(this.options.duration)
      .attr('d', () => {
        const o = { x: source.x, y: source.y };
        return this.diagonal(o, o);
      })
      .remove();

    // Store the old positions for transition.
    this.nodes.forEach(d => {
      d.x0 = d.x;
      d.y0 = d.y;
    });
  }

  /**
   * Wrap text
   * https://bl.ocks.org/mbostock/7555321
   * @param {String} text text data being passed in
   * @param {Number} width value for how wide to set the text
   */
  wrap(text, width, isLink, url) {
    text.each(function() {
      const text = d3.select(this);
      const words = text
        .text()
        .split(/\s+/)
        .reverse();
      let word;
      let line = [];
      let lineNumber = 0;
      const lineHeight = 0.75; // ems
      const x = text.attr('x');
      const y = text.attr('y');
      const dy = 0.35;
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', `${dy}em`)
        .attr('class', 'tree-nav__node-label');

      /*eslint-disable */
      while ((word = words.pop())) {
        /*eslint-enable */
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = text
            .append('tspan')
            .attr('class', 'tree-nav__node-label')
            .attr('x', x)
            .attr('y', y)
            .attr('dy', `${++lineNumber * lineHeight + dy}em`)
            .text(word);
        }
      }

      if (isLink) {
        lineNumber = lineNumber === 1 ? lineNumber : ++lineNumber;

        text
          .append('a')
          .attr('class', 'tree-nav__text-link')
          .attr('href', url)
          .append('tspan')
          .attr('x', x)
          .attr('y', y)
          .attr('dy', `${lineNumber * lineHeight + dy + 0.1}em`)
          .attr('class', 'tree-nav__text-link-label')
          .text('Visit')
          .append('tspan')
          .attr('class', 'tree-nav__arrow')
          .text(' ➜');
      }
    });
  }

  /**
   * Creates a curved (diagonal) path from parent to the child nodes
   * @param {Node} s
   * @param {Node} d
   */
  diagonal(s, d) {
    const path = `M ${s.y} ${s.x}
          C ${(s.y + d.y) / 2} ${s.x},
            ${(s.y + d.y) / 2} ${d.x},
            ${d.y} ${d.x}`;

    return path;
  }

  /**
   * Toggle children on click
   * @param {Node} d children nodes
   */
  click(d) {
    const { target } = d3.event;
    const isTargetVisitLink =
      target.classList.contains('tree-nav__text-link') ||
      target.classList.contains('tree-nav__text-link-label');

    // Bail early if clicking on the visit link
    if (isTargetVisitLink) {
      return;
    }

    d3.event.preventDefault();

    const svg = document.querySelector('.js--tree-nav-svg .tree-nav__group');
    this.coords = d3.mouse(svg);

    if (d.children) {
      // collapse all nodes at the source of the click
      d.children.forEach(this.collapse.bind(this));
      d._children = d.children;
      d.children = null;
    } else {
      d.children = d._children;
      d._children = null;
    }
    this.update(d);
    this.promiseLink().then(() => this.transitionEnd(d));
  }

  /**
   * Promise based end transition callback
   * @param {Node} d child node
   */
  transitionEnd(d) {
    this.resetBounds();
    if (d._children || d.children) {
      this.centerNode(d);
    }
    this.resize(d);
  }

  /**
   * Resizes and animates svg and tree layout based on child node count
   * https://codepen.io/marxtseng/pen/oBVjvB
   * @param {Node} d child node
   */
  resize(d) {
    const height = Math.max(
      this.treeData.descendants().reverse().length * this.options.nodeSpacing,
      this.height,
    );
    const width = this.bounds.width + this.margin.right + this.margin.left;

    this.treemap = d3.tree().size([height, width]);

    d3.select('.js--tree-nav-svg svg')
      .transition()
      .on('end', () => {
        this.resetBounds();
      })
      .attr('width', width)
      .attr('height', height);

    this.update(d);
  }

  resetBounds() {
    this.bounds = d3
      .select('.tree-nav__group')
      .node()
      .getBBox();
  }

  /**
   * If SVG group container is wider than wrapping container, center clicked node
   * @param {Node} d child node
   */
  centerNode(d) {
    const containerWidth = this.container.offsetWidth;
    const endWidth = (this.bounds.width - containerWidth) * -1;
    const nodeCenter = (d.y - containerWidth / 2) * -1;

    if (this.bounds.width > containerWidth) {
      if (d.y <= containerWidth / 2) {
        this.currentDrag = 70;
      } else {
        if (endWidth > nodeCenter) {
          this.currentDrag = endWidth;
        } else {
          this.currentDrag = nodeCenter;
        }
      }

      d3.select('.tree-nav__group')
        .transition()
        .attr('transform', `translate(${this.currentDrag}, ${0})`);
      this.scrollEnd = endWidth;
    } else {
      this.currentDrag = 70;
      d3.select('.tree-nav__group')
        .transition()
        .attr('transform', `translate(${this.currentDrag}, ${0})`);
      this.scrollEnd = this.currentDrag;
    }
  }

  /**
   * Collapses active nodes
   * @param {Node} d children nodes
   */
  collapse(d) {
    if (d.children) {
      d._children = d.children;
      d._children.forEach(this.collapse.bind(this));
      d.children = null;
    }
  }
}

export default TreeNav;
